import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import OnboardingBanner from "@/components/OnboardingBanner";
import { useAuth } from "@/App";
import { motion } from "framer-motion";
import {
  ReceiptX, DownloadSimple, ArrowsClockwise, Info, CheckCircle, Warning,
  Buildings, FileText, ShieldCheck, HandCoins, Receipt, LockSimple
} from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getCurrentFY() {
  const now = new Date();
  const m = now.getMonth() + 1; // 1-12
  const y = now.getFullYear();
  if (m >= 4) return `${y}-${String(y + 1).slice(2)}`;
  return `${y - 1}-${String(y).slice(2)}`;
}

function fyOptions() {
  const current = getCurrentFY();
  const startYear = parseInt(current.split("-")[0]);
  return Array.from({ length: 4 }, (_, i) => {
    const y = startYear - i;
    return `${y}-${String(y + 1).slice(2)}`;
  });
}

function StatCard({ label, value, sub, color = "slate" }) {
  const colors = {
    slate: "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100",
    amber: "bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-300",
    green: "bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700 text-green-700 dark:text-green-300",
    red:   "bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700 text-red-700 dark:text-red-300",
    blue:  "bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300",
  };
  return (
    <div className={`rounded-2xl border p-4 ${colors[color]}`}>
      <p className="text-xs font-medium opacity-70 mb-1">{label}</p>
      <p className="text-xl font-bold font-mono">{value}</p>
      {sub && <p className="text-[11px] opacity-60 mt-0.5">{sub}</p>}
    </div>
  );
}

function TurnoverMeter({ ts }) {
  if (!ts) return null;
  const pct = ts.percent;
  const bar = Math.min(pct, 100);
  const barColor = ts.needs_registration ? "bg-red-500" : ts.approaching ? "bg-amber-400" : "bg-green-500";
  const fmtAmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl p-5 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Annual Turnover — FY {ts.fiscal_year}</p>
          <p className="text-xs text-slate-400 mt-0.5">GST registration mandatory above ₹20 lakhs</p>
        </div>
        <div className="text-right">
          <p className="font-mono font-bold text-lg text-slate-800 dark:text-slate-100">{fmtAmt(ts.annual_turnover)}</p>
          <p className="text-xs text-slate-400">of {fmtAmt(ts.threshold)}</p>
        </div>
      </div>
      <div className="h-2.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${bar}%` }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className={`h-full rounded-full ${barColor}`}
        />
      </div>
      <div className="flex items-center gap-2">
        {ts.needs_registration ? (
          <div className="flex items-center gap-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-1.5">
            <Warning size={13} weight="fill" />
            Turnover exceeds ₹20L — GST registration is mandatory
          </div>
        ) : ts.approaching ? (
          <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-1.5">
            <Warning size={13} weight="fill" />
            {pct.toFixed(0)}% of threshold — consider registering proactively
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded-xl px-3 py-1.5">
            <CheckCircle size={13} weight="fill" />
            {pct.toFixed(0)}% of threshold — registration not yet required
          </div>
        )}
      </div>
    </div>
  );
}

const GSTR1_PLANS = ["basic", "starter", "pro", "agency", "admin"];

export default function GSTCompliance() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canExportGSTR1 = GSTR1_PLANS.includes(user?.plan);
  const [period, setPeriod]   = useState("fy");
  const [month, setMonth]     = useState(new Date().toISOString().slice(0, 7));
  const [fy, setFy]           = useState(getCurrentFY());
  const [data, setData]       = useState(null);
  const [tds, setTds]         = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab]         = useState("summary");

  const fetchData = async () => {
    setLoading(true);
    try {
      const gstParam  = period === "month" ? `month=${month}` : `fy=${fy}`;
      const tdsParam  = `fy=${fy}`;
      const [gstRes, tdsRes] = await Promise.all([
        fetch(`${API}/gst/summary?${gstParam}`, { credentials: "include" }),
        fetch(`${API}/gst/tds-summary?${tdsParam}`, { credentials: "include" }),
      ]);
      if (gstRes.ok) setData(await gstRes.json());
      if (tdsRes.ok) setTds(await tdsRes.json());
    } catch {
      toast.error("Failed to load GST data");
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [period, month, fy]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportGSTR1 = async () => {
    const rs = data?.turnover_status?.registration_status;
    if (rs === "not_required" || rs === "approaching") {
      toast.info("GSTR-1 not applicable — your turnover is below the ₹20L threshold. No filing needed.");
      return;
    }
    const param = period === "month" ? `month=${month}` : `fy=${fy}`;
    const token = localStorage.getItem("session_token");
    try {
      const res = await fetch(`${API}/gst/gstr1?${param}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${res.status}`);
      }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `GSTR1_${period === "month" ? month : fy}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("GSTR-1 ZIP downloaded — contains b2cs.csv, b2cl.csv, hsn.csv for GST Offline Tool");
    } catch (e) {
      toast.error(e.message || "Export failed");
    }
  };

  const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const ecoBkgs     = data?.bookings.filter(b => b.liable_party === "platform") ?? [];
  const directBkgs  = data?.bookings.filter(b => b.liable_party === "host")     ?? [];

  return (
    <div className="space-y-6 max-w-5xl">
      <OnboardingBanner pageKey="gst" />
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-manrope text-2xl font-bold text-slate-900 dark:text-amber-400 tracking-tight">GST Compliance</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Booking-wise GST, GSTR-1 export &amp; TDS reconciliation — Indian fiscal year (Apr–Mar).</p>
        </div>
        {canExportGSTR1 ? (
          <button onClick={exportGSTR1}
            className="flex items-center gap-2 px-4 py-2 bg-amber-400 hover:bg-amber-500 text-white text-sm font-semibold rounded-xl transition">
            <DownloadSimple size={15} weight="bold" /> Export GSTR-1 (ZIP)
          </button>
        ) : (
          <button onClick={() => navigate("/billing")}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-amber-50 dark:hover:bg-amber-900/20 text-slate-600 dark:text-slate-300 hover:text-amber-600 text-sm font-semibold rounded-xl border border-slate-200 dark:border-slate-600 hover:border-amber-300 transition">
            <LockSimple size={15} weight="fill" /> Export GSTR-1 — Starter+
          </button>
        )}
      </div>

      {/* ECO Rule Banner — always visible */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-4 flex gap-3">
        <Info size={16} className="text-blue-500 shrink-0 mt-0.5" />
        <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1.5">
          <p className="font-bold text-sm">ECO Rule — Section 9(5) CGST Act</p>
          <p>
            <span className="font-semibold">Airbnb, Booking.com, MakeMyTrip &amp; OYO</span> are Electronic Commerce Operators (ECOs).
            Under Section 9(5) CGST Act, <span className="font-semibold">the platform collects and remits GST</span> on your behalf.
            You have <span className="font-semibold">no GST liability</span> on these bookings — even if you are GST-registered.
          </p>
          <p>
            <span className="font-semibold">Direct / Other bookings:</span> You collect GST from the guest and remit it — only if your annual turnover exceeds ₹20 lakhs.
          </p>
          <p className="border-t border-blue-200 dark:border-blue-600 pt-1.5 mt-1">
            <span className="font-semibold">GST rates (SAC 998313):</span> ≤ ₹7,500/night → 12% &nbsp;|&nbsp; &gt; ₹7,500/night → 18%
            &nbsp;·&nbsp; Always CGST + SGST (Place of Supply = property location, Sec 12(3) IGST Act)
            &nbsp;·&nbsp; <span className="font-semibold">TDS u/s 194-O:</span> 1% withheld by ECO platforms (5% if no PAN)
          </p>
        </div>
      </div>

      {/* Period selector */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex rounded-xl overflow-hidden border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800">
          {[{ id: "fy", label: "Fiscal Year" }, { id: "month", label: "Monthly" }].map(p => (
            <button key={p.id} onClick={() => setPeriod(p.id)}
              className={`px-4 py-2 text-sm font-medium transition ${period === p.id ? "bg-white dark:bg-slate-700 shadow-sm text-amber-600" : "text-slate-600 dark:text-slate-300"}`}>
              {p.label}
            </button>
          ))}
        </div>

        {period === "month" ? (
          <input type="month" value={month} onChange={e => setMonth(e.target.value)}
            className="px-3 py-2 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-100 focus:outline-none focus:border-amber-400" />
        ) : (
          <select value={fy} onChange={e => setFy(e.target.value)}
            className="px-3 py-2 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-100 focus:outline-none focus:border-amber-400">
            {fyOptions().map(y => <option key={y} value={y}>FY {y} (Apr {y.split("-")[0]} – Mar 20{y.split("-")[1]})</option>)}
          </select>
        )}

        <button onClick={fetchData} disabled={loading}
          className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-xl bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-200 transition disabled:opacity-40">
          <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-2 border-b border-slate-100 dark:border-slate-700">
        {[
          { id: "summary", label: "GST Summary" },
          { id: "tds",     label: "TDS Reconciliation" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition -mb-px ${tab === t.id ? "border-amber-400 text-amber-600" : "border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-20 skeleton rounded-2xl" />)}
        </div>
      ) : tab === "summary" && data ? (
        <>
          {/* Turnover meter */}
          <TurnoverMeter ts={data.turnover_status} />

          {/* Registration status banner */}
          {(() => {
            const rs = data.turnover_status?.registration_status;
            if (data.gstin) return (
              <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-100 rounded-xl px-4 py-2">
                <CheckCircle size={14} weight="fill" />
                GSTIN registered: <span className="font-mono font-semibold ml-1">{data.gstin}</span>
                {data.pan && <span className="ml-3 text-green-600">· PAN: {data.pan}</span>}
              </div>
            );
            if (rs === "overdue") return (
              <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                <Warning size={14} weight="fill" className="shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold">GST Registration Required — Your turnover has crossed ₹20 lakhs</p>
                  <p className="mt-0.5">You must register for GST immediately. The amounts below are your <strong>estimated overdue liability</strong>. Register at <a href="https://www.gst.gov.in" target="_blank" rel="noreferrer" className="underline">gst.gov.in</a> or through a CA. Add your GSTIN in <a href="/settings" className="underline font-semibold">Settings</a> once done.</p>
                </div>
              </div>
            );
            if (rs === "approaching") return (
              <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <Warning size={14} weight="fill" className="shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold">Approaching GST Threshold — Start the registration process now</p>
                  <p className="mt-0.5">You have <strong>no current GST liability</strong> yet, but you're close to the ₹20L limit. Amounts below are shown for reference. Registration can take 7–10 days — apply proactively to avoid a gap. Add GSTIN in <a href="/settings" className="underline">Settings</a> when ready.</p>
                </div>
              </div>
            );
            return (
              <div className="flex items-start gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
                <Info size={14} className="shrink-0 mt-0.5 text-slate-400" />
                <div>
                  <p className="font-semibold">Not GST Registered — No liability below ₹20 lakh threshold</p>
                  <p className="mt-0.5 text-slate-500">Your annual turnover is below ₹20,00,000 so you are <strong>not required to collect or remit GST</strong>. The amounts shown for direct bookings are <span className="italic">reference projections only</span> — not actual tax owed. Add your GSTIN in <a href="/settings" className="underline">Settings</a> when you register.</p>
                </div>
              </div>
            );
          })()}

          {/* GSTIN state advisory */}
          {data.gstin && data.host_state && (
            <div className="flex items-start gap-2 text-xs bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-xl px-4 py-3">
              <Info size={14} className="text-slate-400 shrink-0 mt-0.5" />
              <span className="text-slate-600 dark:text-slate-300">
                Your GSTIN is registered in <strong>{data.host_state.replace(/\b\w/g, c => c.toUpperCase())}</strong>.
                {directBkgs.some(b => b.supply_type === "interstate")
                  ? <> Some direct bookings are in a <strong className="text-orange-600"> different state</strong> — those use <strong>IGST</strong> (not CGST+SGST). Consider registering a GSTIN in each property state (Sec 25(1) CGST).</>
                  : <> All direct bookings are in the same state — <strong className="text-green-600">CGST+SGST applies</strong>.</>
                }
              </span>
            </div>
          )}
          {data.gstin && !data.host_state && (
            <div className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 text-amber-700">
              <Warning size={13} weight="fill" />
              Could not determine state from GSTIN. Defaulting to intrastate. Verify your GSTIN in Settings.
            </div>
          )}

          {/* Host vs Platform totals */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Your liability */}
            {(() => {
              const rs = data.turnover_status?.registration_status;
              const isReference = rs === "not_required" || rs === "approaching";
              const cardStyle = rs === "overdue"
                ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700"
                : isReference
                  ? "bg-slate-50 dark:bg-slate-700/40 border-slate-200 dark:border-slate-600"
                  : "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700";
              const refTotals = isReference && data.reference_host_totals ? data.reference_host_totals : null;
              return (
            <div className={`rounded-2xl border p-5 space-y-3 ${cardStyle}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Receipt size={18} className={rs === "overdue" ? "text-red-600" : isReference ? "text-slate-400" : "text-amber-600"} weight="duotone" />
                  <p className={`text-sm font-semibold ${rs === "overdue" ? "text-red-800 dark:text-red-300" : isReference ? "text-slate-500 dark:text-slate-400" : "text-amber-800 dark:text-amber-300"}`}>
                    {rs === "overdue" ? "Overdue GST Liability" : isReference ? "Projected GST (Reference Only)" : "Your GST Liability (Direct Bookings)"}
                  </p>
                </div>
                {isReference && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-300 font-semibold">NOT OWED</span>
                )}
                {rs === "overdue" && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200 font-semibold">REGISTER NOW</span>
                )}
              </div>
              {/* Show actual liability (registered / overdue) or reference amounts (not_required / approaching) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-slate-500 mb-0.5">Taxable Revenue</p>
                  <p className="font-mono font-bold text-slate-800 dark:text-slate-100">{fmt(data.host_totals.taxable)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-0.5">{isReference ? "GST if registered" : "Total GST You Owe"}</p>
                  <p className={`font-mono font-bold ${isReference ? "text-slate-400 dark:text-slate-500" : rs === "overdue" ? "text-red-700 dark:text-red-400" : "text-amber-900 dark:text-amber-200"}`}>
                    {isReference ? fmt(refTotals?.total_gst ?? 0) : fmt(data.host_totals.total_gst)}
                  </p>
                </div>
                {(isReference ? (refTotals?.igst ?? 0) : data.host_totals.igst) > 0 ? (
                  <div className="col-span-2">
                    <p className="text-xs text-orange-500 mb-0.5">IGST (interstate)</p>
                    <p className={`font-mono text-sm font-semibold ${isReference ? "text-slate-400" : "text-orange-700 dark:text-orange-400"}`}>
                      {fmt(isReference ? refTotals?.igst : data.host_totals.igst)}
                    </p>
                  </div>
                ) : (
                  <>
                    <div>
                      <p className="text-xs text-slate-500 mb-0.5">CGST</p>
                      <p className={`font-mono text-sm ${isReference ? "text-slate-400" : "text-amber-800 dark:text-amber-300"}`}>
                        {fmt(isReference ? refTotals?.cgst : data.host_totals.cgst)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-0.5">SGST</p>
                      <p className={`font-mono text-sm ${isReference ? "text-slate-400" : "text-amber-800 dark:text-amber-300"}`}>
                        {fmt(isReference ? refTotals?.sgst : data.host_totals.sgst)}
                      </p>
                    </div>
                  </>
                )}
              </div>
              {data.itc && !isReference && (
                <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-600 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Output GST (on income)</span>
                    <span className="font-mono text-slate-700 dark:text-slate-200">{fmt(data.itc.output_gst)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-emerald-600 dark:text-emerald-400">Less: Input Tax Credit (ITC)</span>
                    <span className="font-mono text-emerald-600 dark:text-emerald-400">− {fmt(data.itc.claimable_itc)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-semibold pt-1 border-t border-dashed border-slate-200 dark:border-slate-600">
                    <span className="text-slate-800 dark:text-slate-100">Net GST Payable</span>
                    <span className={`font-mono ${data.itc.net_gst_payable > 0 ? "text-amber-900 dark:text-amber-200" : "text-emerald-600 dark:text-emerald-400"}`}>
                      {data.itc.net_gst_payable < 0 ? `${fmt(Math.abs(data.itc.net_gst_payable))} credit c/f` : fmt(data.itc.net_gst_payable)}
                    </span>
                  </div>
                </div>
              )}
              <p className="text-[10px] text-slate-400 border-t border-slate-200 dark:border-slate-600 pt-2">
                {directBkgs.length} direct booking{directBkgs.length !== 1 ? "s" : ""}
                {isReference ? " · No filing obligation — reference only" : " · File in GSTR-1 and GSTR-3B"}
              </p>
            </div>
              );
            })()}

            {/* Platform liability */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-2xl p-5 space-y-3">
              <div className="flex items-center gap-2">
                <ShieldCheck size={18} className="text-blue-600" weight="duotone" />
                <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">Platform Pays GST (ECO Bookings)</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mb-0.5">Revenue via ECO</p>
                  <p className="font-mono font-bold text-blue-900 dark:text-blue-200">{fmt(data.platform_totals.taxable)}</p>
                </div>
                <div>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mb-0.5">GST (Platform Remits)</p>
                  <p className="font-mono font-bold text-blue-900 dark:text-blue-200">{fmt(data.platform_totals.total_gst)}</p>
                </div>
                <div>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mb-0.5">TDS Withheld</p>
                  <p className="font-mono text-sm text-blue-800 dark:text-blue-300">{fmt(data.platform_totals.tds)}</p>
                </div>
                <div>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mb-0.5">Your Liability</p>
                  <p className="font-mono text-sm font-bold text-green-700 dark:text-green-400">₹0.00</p>
                </div>
              </div>
              <p className="text-[10px] text-blue-600 dark:text-blue-500 border-t border-blue-200 dark:border-blue-700 pt-2">
                {ecoBkgs.length} ECO booking{ecoBkgs.length !== 1 ? "s" : ""} via Airbnb/Booking.com/MakeMyTrip/OYO · Sec 9(5) CGST
              </p>
            </div>
          </div>

          {/* Booking table */}
          {data.bookings.length > 0 ? (
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-50 dark:border-slate-700 flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                  {data.bookings.length} Bookings · SAC 998313
                </p>
                <div className="flex items-center gap-3 text-[10px]">
                  <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-semibold">
                    <HandCoins size={11} /> You pay GST
                  </span>
                  <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-semibold">
                    <ShieldCheck size={11} /> Platform pays
                  </span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50 dark:bg-slate-700/60 text-slate-400 uppercase tracking-wide text-[10px]">
                      <th className="px-4 py-2.5 text-left font-semibold">Guest</th>
                      <th className="px-4 py-2.5 text-left font-semibold">Property</th>
                      <th className="px-4 py-2.5 text-left font-semibold">Check-in</th>
                      <th className="px-4 py-2.5 text-left font-semibold">Platform</th>
                      <th className="px-4 py-2.5 text-right font-semibold">Amount</th>
                      <th className="px-4 py-2.5 text-center font-semibold">Rate</th>
                      <th className="px-4 py-2.5 text-right font-semibold">CGST</th>
                      <th className="px-4 py-2.5 text-right font-semibold">SGST</th>
                      <th className="px-4 py-2.5 text-right font-semibold text-orange-500">IGST</th>
                      <th className="px-4 py-2.5 text-right font-semibold">Total GST</th>
                      <th className="px-4 py-2.5 text-center font-semibold">Liable</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50 dark:divide-slate-700/50">
                    {data.bookings.map((b, i) => {
                      const isEco = b.liable_party === "platform";
                      return (
                        <motion.tr key={b.booking_id || i}
                          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                          transition={{ delay: i * 0.015 }}
                          className={`transition-colors ${isEco ? "bg-blue-50/40 dark:bg-blue-900/10 hover:bg-blue-50 dark:hover:bg-blue-900/20" : "hover:bg-amber-50/40 dark:hover:bg-amber-900/10"}`}>
                          <td className="px-4 py-2.5 text-slate-700 dark:text-slate-200 font-medium">{b.guest_name || "—"}</td>
                          <td className="px-4 py-2.5 text-slate-500 dark:text-slate-400 max-w-[140px] truncate">{b.property_name || b.property_id}</td>
                          <td className="px-4 py-2.5 text-slate-500 dark:text-slate-400">{b.check_in}</td>
                          <td className="px-4 py-2.5 text-slate-500 dark:text-slate-400">{b.platform || "—"}</td>
                          <td className="px-4 py-2.5 font-mono text-right text-slate-800 dark:text-slate-100">{fmt(b.amount)}</td>
                          <td className="px-4 py-2.5 text-center">
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${b.gst_rate === 18 ? "bg-orange-50 text-orange-600 border border-orange-200" : "bg-green-50 text-green-600 border border-green-200"}`}>
                              {b.gst_rate}%
                            </span>
                          </td>
                          <td className="px-4 py-2.5 font-mono text-right text-slate-500 dark:text-slate-400">
                            {isEco || b.supply_type === "interstate" ? "—" : fmt(b.cgst)}
                          </td>
                          <td className="px-4 py-2.5 font-mono text-right text-slate-500 dark:text-slate-400">
                            {isEco || b.supply_type === "interstate" ? "—" : fmt(b.sgst)}
                          </td>
                          <td className="px-4 py-2.5 font-mono text-right">
                            {!isEco && b.supply_type === "interstate"
                              ? <span className="text-orange-600 font-semibold">{fmt(b.igst)}</span>
                              : <span className="text-slate-300 dark:text-slate-600">—</span>
                            }
                          </td>
                          <td className={`px-4 py-2.5 font-mono text-right font-semibold ${isEco ? "text-blue-400 dark:text-blue-500" : "text-amber-700 dark:text-amber-400"}`}>
                            {fmt(b.total_gst)}
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            {isEco ? (
                              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700">
                                <ShieldCheck size={10} /> Platform
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700">
                                <HandCoins size={10} /> You
                              </span>
                            )}
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-50 dark:bg-slate-700/60 font-semibold text-slate-600 dark:text-slate-300 text-xs border-t border-slate-200 dark:border-slate-600">
                      <td colSpan={4} className="px-4 py-3 text-slate-500">TOTAL ({data.bookings.length} bookings)</td>
                      <td className="px-4 py-3 font-mono text-right text-slate-800 dark:text-slate-100">
                        {fmt((data.host_totals.taxable || 0) + (data.platform_totals.taxable || 0))}
                      </td>
                      <td />
                      <td className="px-4 py-3 font-mono text-right text-amber-700 dark:text-amber-400">{fmt(data.host_totals.cgst)}</td>
                      <td className="px-4 py-3 font-mono text-right text-amber-700 dark:text-amber-400">{fmt(data.host_totals.sgst)}</td>
                      <td className="px-4 py-3 font-mono text-right text-orange-600">
                        {data.host_totals.igst > 0 ? fmt(data.host_totals.igst) : "—"}
                      </td>
                      <td className="px-4 py-3 font-mono text-right">
                        <div className="text-amber-700 dark:text-amber-400">You: {fmt(data.host_totals.total_gst)}</div>
                        <div className="text-blue-500 text-[10px]">Platform: {fmt(data.platform_totals.total_gst)}</div>
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-16 text-slate-400">
              <ReceiptX size={36} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">No bookings found for this period.</p>
            </div>
          )}
        </>

      ) : tab === "tds" && tds ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard label="Total TDS Withheld" value={fmt(tds.total_tds_withheld)} sub="by ECO platforms @1% (Sec 194-O)" color="red" />
            <StatCard label="ECO Platform Revenue" value={fmt(tds.monthly.reduce((s, r) => s + (r.eco_revenue || 0), 0))} sub="Airbnb / Booking.com / MMT / OYO" color="blue" />
            <StatCard label="Net Received (after TDS)" value={fmt(tds.monthly.reduce((s, r) => s + r.net_received, 0))} color="green" />
          </div>

          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-4 text-xs text-amber-800 dark:text-amber-300">
            <strong>Note:</strong> {tds.note}
          </div>

          {tds.monthly.length > 0 ? (
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50 dark:bg-slate-700/60 text-slate-400 uppercase tracking-wide text-[10px]">
                      <th className="px-4 py-2.5 text-left font-semibold">Month</th>
                      <th className="px-4 py-2.5 text-right font-semibold">Bookings</th>
                      <th className="px-4 py-2.5 text-right font-semibold">Gross Revenue</th>
                      <th className="px-4 py-2.5 text-right font-semibold">ECO Revenue</th>
                      <th className="px-4 py-2.5 text-right font-semibold">Direct Revenue</th>
                      <th className="px-4 py-2.5 text-right font-semibold">TDS @1%</th>
                      <th className="px-4 py-2.5 text-right font-semibold">Net Received</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50 dark:divide-slate-700/50">
                    {tds.monthly.map((r, i) => (
                      <motion.tr key={r.month} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                        transition={{ delay: i * 0.03 }}
                        className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                        <td className="px-4 py-2.5 text-slate-700 dark:text-slate-200 font-medium">{r.month}</td>
                        <td className="px-4 py-2.5 text-slate-500 dark:text-slate-400 text-right">{r.bookings}</td>
                        <td className="px-4 py-2.5 font-mono text-right text-slate-800 dark:text-slate-100">{fmt(r.gross_revenue)}</td>
                        <td className="px-4 py-2.5 font-mono text-right text-blue-600 dark:text-blue-400">{fmt(r.eco_revenue)}</td>
                        <td className="px-4 py-2.5 font-mono text-right text-amber-600 dark:text-amber-400">{fmt(r.direct_revenue)}</td>
                        <td className="px-4 py-2.5 font-mono text-right text-red-600">&#8722;{fmt(r.tds_withheld)}</td>
                        <td className="px-4 py-2.5 font-mono text-right font-semibold text-green-700 dark:text-green-400">{fmt(r.net_received)}</td>
                      </motion.tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-50 dark:bg-slate-700/60 font-semibold text-slate-600 dark:text-slate-300 text-xs border-t border-slate-200 dark:border-slate-600">
                      <td className="px-4 py-3" colSpan={2}>TOTAL</td>
                      <td className="px-4 py-3 font-mono text-right">{fmt(tds.monthly.reduce((s, r) => s + r.gross_revenue, 0))}</td>
                      <td className="px-4 py-3 font-mono text-right text-blue-600">{fmt(tds.monthly.reduce((s, r) => s + (r.eco_revenue || 0), 0))}</td>
                      <td className="px-4 py-3 font-mono text-right text-amber-600">{fmt(tds.monthly.reduce((s, r) => s + (r.direct_revenue || 0), 0))}</td>
                      <td className="px-4 py-3 font-mono text-right text-red-600">&#8722;{fmt(tds.total_tds_withheld)}</td>
                      <td className="px-4 py-3 font-mono text-right text-green-700 dark:text-green-400">{fmt(tds.monthly.reduce((s, r) => s + r.net_received, 0))}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-16 text-slate-400">
              <FileText size={36} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">No booking data found for FY {tds.fiscal_year || fy}.</p>
            </div>
          )}
        </>

      ) : null}
    </div>
  );
}
